import { Icons } from '@/components/icons';
import { TransportationTypeIcon } from '@/components/icons/TransportationTypeIcon';
import TicketCuticle from '@/components/manage/TicketCuticle';
import ProductDetails from '@/components/manage/tickets/ProductDetails';
import TransferTime from '@/components/manage/tickets/TransferTime';
import { Badge } from '@/components/primitives/Badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/primitives/Tooltip';
import { Typography } from '@/components/primitives/Typography';
import { BackgroundThemeProvider } from '@/context/BackgroundThemeContextProvider';
import { TransText } from '@/i18n/trans/text';
import type { AdmissionDTO, LegDTO } from '@/types/dto/agent';
import type { CheckoutOrBookingPassengerMap } from '@/types/passenger';
import {
  areAllAdmissionsWithStatus,
  areRefundedLike,
  areSomeAdmissionsWithStatus,
} from '@/utils/admission';
import { breakpoints } from '@/utils/breakpoints';
import { cn } from '@/utils/cn';
import {
  format,
  formatDuration,
  formatTime,
  getDurationBetweenTimestamps,
} from '@/utils/dateTime';
import { getPtModeIconName } from '@/utils/icon';
import { type FC, useMemo } from 'react';
import { useMediaQuery } from 'react-responsive';

interface LegCardProps {
  leg: LegDTO;
  nextLeg?: LegDTO;
  admissions: Array<AdmissionDTO>;
  passengersMap: CheckoutOrBookingPassengerMap;
}

const LegCard: FC<LegCardProps> = ({
  leg,
  nextLeg,
  admissions,
  passengersMap,
}) => {
  const isLaptopOrBigger = useMediaQuery({
    minWidth: breakpoints.laptop,
  });

  const allExchanged = areAllAdmissionsWithStatus(admissions, 'EXCHANGED');
  const refundedLike = areRefundedLike(admissions);

  const badgeType = useMemo(() => {
    const someRefunded = areSomeAdmissionsWithStatus(admissions, 'REFUNDED');
    const someReleased = areSomeAdmissionsWithStatus(admissions, 'RELEASED');
    const someFulfilled = areSomeAdmissionsWithStatus(admissions, 'FULFILLED');

    if (refundedLike) {
      return 'refunded';
    } else if (someRefunded && someFulfilled) {
      return 'partiallyRefunded';
    } else if (someReleased && !someRefunded) {
      return 'partiallyCancelled';
    }
    return null;
  }, [admissions, refundedLike]);

  const transferTime = nextLeg
    ? getDurationBetweenTimestamps(leg.arrivalTime, nextLeg.departureTime)
    : null;

  const renderTransportBadge = () => (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge
            variant={refundedLike ? 'disabled' : 'light'}
            className="rounded-sm"
          >
            <TransportationTypeIcon ptMode={leg.ptMode} />
            <Typography variant="body2-bold">{leg.lineNumber}</Typography>
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <Typography variant="body2">
            <TransText i18nKey={getPtModeIconName(leg.ptMode)} />
          </Typography>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  const renderTripDetails = () => (
    <div className="flex flex-row gap-2">
      <div className="flex w-fit flex-col self-center">
        <Typography variant="times" asChild>
          <h2>{formatTime(leg.departureTime)}</h2>
        </Typography>
        <Typography variant="body2">{leg.originStop?.name}</Typography>
      </div>
      <div
        className={cn(
          'mt-2 flex h-1/2 w-fit flex-row items-center gap-0.5 self-center text-neutral',
          {
            '-mt-3': isLaptopOrBigger,
          }
        )}
      >
        <Icons.line />
        <Typography variant="caption" className="whitespace-nowrap">
          {formatDuration(leg.duration)}
        </Typography>
        <Icons.line />
      </div>
      <div className="flex w-fit flex-col self-center">
        <Typography variant="times" asChild>
          <h2>{formatTime(leg.arrivalTime)}</h2>
        </Typography>
        <Typography variant="body2">{leg.destinationStop?.name}</Typography>
      </div>
    </div>
  );

  const renderDate = () =>
    leg.departureTime && (
      <Typography variant="body2" className="text-neutral">
        {format(leg.departureTime, 'EEE, dd MMM yyyy')}
      </Typography>
    );

  const renderMobile = () => {
    return (
      <div
        className={cn(
          'flex flex-col rounded-lg border border-neutral-light bg-white text-dark',
          { 'bg-neutral-pale text-neutral': refundedLike }
        )}
      >
        <div className="flex flex-row items-center justify-between p-3">
          {renderTransportBadge()}
          {renderDate()}
        </div>
        <div className="px-3 pb-3">{renderTripDetails()}</div>
        <div className="relative py-3">
          <TicketCuticle isVertical={false} />
          {(refundedLike ||
            badgeType === 'partiallyRefunded' ||
            badgeType === 'partiallyCancelled') && (
            <div className="absolute right-3 top-1/2 z-10 -translate-y-1/2">
              {refundedLike && (
                <Badge variant="disabled" className="h-1/4">
                  <Icons.ticketReturn height={16} width={16} />
                  <TransText i18nKey="refunded" />
                </Badge>
              )}
              {badgeType === 'partiallyRefunded' && (
                <Badge variant="attention" className="h-1/4">
                  <Icons.infoCircle height={16} width={16} />
                  <TransText i18nKey="partiallyRefunded" />
                </Badge>
              )}
              {badgeType === 'partiallyCancelled' && (
                <Badge variant="attention" className="h-1/4">
                  <Icons.infoCircle height={16} width={16} />
                  <TransText i18nKey="partiallyCancelled" />
                </Badge>
              )}
            </div>
          )}
        </div>
        <div className="px-3 pb-3">
          <ProductDetails
            admissions={admissions}
            passengersMap={passengersMap}
          />
        </div>
      </div>
    );
  };

  const renderLaptopOrBigger = () => {
    return (
      <div
        className={cn(
          'relative my-3 flex flex-row rounded-lg border border-neutral-light bg-white text-dark laptop:flex-row',
          { 'bg-neutral-pale text-neutral': refundedLike }
        )}
      >
        <div className="flex w-full flex-row justify-between">
          <div className="flex flex-row gap-2 p-4">{renderTripDetails()}</div>
          {refundedLike && (
            <Badge variant="disabled" className="mt-3 h-1/4">
              <Icons.ticketReturn height={16} width={16} />
              <TransText i18nKey="refunded" />
            </Badge>
          )}
          {badgeType === 'partiallyRefunded' && (
            <Badge variant="attention" className="mt-3 h-1/4">
              <Icons.infoCircle height={16} width={16} />
              <TransText i18nKey="partiallyRefunded" />
            </Badge>
          )}
          {badgeType === 'partiallyCancelled' && (
            <Badge variant="attention" className="mt-3 h-1/4">
              <Icons.infoCircle height={16} width={16} />
              <TransText i18nKey="partiallyCancelled" />
            </Badge>
          )}
        </div>

        <div className="pl-4">
          <TicketCuticle isVertical />
        </div>

        <div className="flex w-2/3 flex-col gap-4 p-3 pl-2">
          <div className="flex flex-row items-center justify-between pl-2">
            {renderTransportBadge()}
            {renderDate()}
          </div>
          <ProductDetails
            admissions={admissions}
            passengersMap={passengersMap}
          />
        </div>
      </div>
    );
  };

  if (allExchanged) {
    return null;
  }

  return (
    <div className="flex flex-col">
      <BackgroundThemeProvider backgroundTheme="white">
        {isLaptopOrBigger ? renderLaptopOrBigger() : renderMobile()}
      </BackgroundThemeProvider>
      {Boolean(transferTime) && (
        <TransferTime
          formattedTime={transferTime}
          arrivalStation={leg.destinationStop?.name}
          departureStation={nextLeg?.originStop?.name}
        />
      )}
    </div>
  );
};

export default LegCard;
